// JobTeaser API V2 (https://docs.jobteaser.com/docs/openapi-b2b): values shared by the API and the app

/** Current offerings first; the legacy ones are kept for the firms still on an old contract */
export const JOBTEASER_PUBLICATION_TYPES = [
  'PUBLICATION_TYPE_STANDARD_CREDIT',
  'PUBLICATION_TYPE_PREMIUM_CREDIT',
  'PUBLICATION_TYPE_SPONSORED_CREDIT',
  'PUBLICATION_TYPE_STANDARD_SLOT',
  'PUBLICATION_TYPE_PREMIUM_SLOT',
  'PUBLICATION_TYPE_FREE',
  'PUBLICATION_TYPE_CLASSIC',
  'PUBLICATION_TYPE_SLOT',
  'PUBLICATION_TYPE_FLASH',
] as const
export type TJobTeaserPublicationType = (typeof JOBTEASER_PUBLICATION_TYPES)[number]

export const JOBTEASER_LEGACY_PUBLICATION_TYPES: readonly TJobTeaserPublicationType[] = [
  'PUBLICATION_TYPE_CLASSIC',
  'PUBLICATION_TYPE_SLOT',
  'PUBLICATION_TYPE_FLASH',
]

export const JOBTEASER_STUDY_LEVELS = [
  'STUDY_LEVEL_IRRELEVANT',
  'STUDY_LEVEL_SECONDARY_EDUCATION_LEVEL',
  'STUDY_LEVEL_SHORT_CYCLE_TERTIARY_EDUCATION',
  'STUDY_LEVEL_BACHELOR',
  'STUDY_LEVEL_MASTER',
  'STUDY_LEVEL_DOCTORATE',
] as const
export type TJobTeaserStudyLevel = (typeof JOBTEASER_STUDY_LEVELS)[number]

export const JOBTEASER_TARGETING_STRATEGIES = [
  'TARGETING_STRATEGY_PUBLIC',
  'TARGETING_STRATEGY_COUNTRY',
  'TARGETING_STRATEGY_SCHOOL',
  'TARGETING_STRATEGY_SCHOOL_BASKET',
] as const
export type TJobTeaserTargetingStrategy = (typeof JOBTEASER_TARGETING_STRATEGIES)[number]

/** Contract durations JobTeaser accepts, in months */
export const JOBTEASER_CONTRACT_DURATIONS = [1, 2, 3, 4, 5, 6, 9, 12, 18, 24, 30, 36, 48] as const

export const JOBTEASER_CANDIDACY_TYPES = ['external', 'internal'] as const
export type TJobTeaserCandidacyType = (typeof JOBTEASER_CANDIDACY_TYPES)[number]

/** What JobTeaser asks the candidate for when they apply on JobTeaser (internal candidacy) */
export const JOBTEASER_DOCUMENTS_REQUESTED = [
  'INTERNAL_CANDIDACY_DOCUMENT_REQUESTED_CV_ONLY',
  'INTERNAL_CANDIDACY_DOCUMENT_REQUESTED_TEXT',
  'INTERNAL_CANDIDACY_DOCUMENT_REQUESTED_COVER_LETTER',
] as const
export type TJobTeaserDocumentRequested = (typeof JOBTEASER_DOCUMENTS_REQUESTED)[number]

export type TJobTeaserTargeting = {
  strategy: TJobTeaserTargetingStrategy
  /** ISO 3166-1 alpha-2, for TARGETING_STRATEGY_COUNTRY */
  countryCodes?: string[]
  /** For TARGETING_STRATEGY_SCHOOL; the names are kept for the display */
  schools?: { id: string, name: string }[]
  /** For TARGETING_STRATEGY_SCHOOL_BASKET */
  schoolBasket?: { id: string, name: string } | null
}

/** Non-secret settings of the firm's JobTeaser site (the credentials are stored encrypted apart) */
export type TJobTeaserSettings = {
  /** UUID of the firm's company on JobTeaser (hiring_organization.id) */
  companyId?: string | null
  defaultPublicationType?: TJobTeaserPublicationType | null
  candidacy?: {
    type: TJobTeaserCandidacyType
    notificationEmail?: string | null
    documentRequested?: TJobTeaserDocumentRequested | null
  }
  targeting?: TJobTeaserTargeting | null
}

/** Options of one offer for JobTeaser (JobOffer.channelOptions.jobteaser) */
export type TJobTeaserOfferOptions = {
  category?: { id: string, name: string } | null
  studyLevels?: TJobTeaserStudyLevel[]
  contractDurationMonths?: number | null
  /** Publication of the job ad when it is created; null: the firm's default */
  publicationType?: TJobTeaserPublicationType | null
  /** false: the job ad is created without a publication (JobTeaser "unpublished" tab), to publish it later */
  publishOnCreate?: boolean
  /** null: the firm's default targeting */
  targeting?: TJobTeaserTargeting | null
}

/** Connection of the firm to JobTeaser, as GET /api/jobBoards/channels describes it (never the secrets) */
export type TJobTeaserConnection = {
  companyId: string | null
  /** The firm entered its own client ID and secret */
  hasFirmCredentials: boolean
  /** BestConsultant's partner credentials are configured on this server */
  partnerCredentialsAvailable: boolean
  /** Credentials and, with the partner credentials, the company id are there */
  isConnected: boolean
}

export const JOBTEASER_ISSUE_CODES = [
  'notReady',
  'notEnabled',
  'notConnected',
  'companyMissing',
  'titleMissing',
  'descriptionMissing',
  'contractMissing',
  'categoryMissing',
  'locationMissing',
  'notificationEmailMissing',
  'countriesMissing',
  'schoolsMissing',
  'schoolBasketMissing',
  'salaryInverted',
  'durationRecommended',
  'publicationTypeMissing',
] as const
export type TJobTeaserIssueCode = (typeof JOBTEASER_ISSUE_CODES)[number]

export type TJobBoardPostingIssue = {
  code: TJobTeaserIssueCode
  /** error: the offer is not sent; warning: it is sent, the firm should check */
  severity: 'error' | 'warning'
}

export const JOBTEASER_JOB_AD_STATUSES = ['JOB_AD_STATUS_ACCEPTED', 'JOB_AD_STATUS_PENDING', 'JOB_AD_STATUS_REJECTED', 'JOB_AD_STATUS_UNSPECIFIED'] as const

/** Posting of one offer on a board posted through its API (GET /api/jobBoards/offers/:offerId/postings) */
export type TJobBoardPosting = {
  channel: 'jobteaser'
  posting: null | {
    jobAdId: string
    status: (typeof JOBTEASER_JOB_AD_STATUSES)[number] | null
    isActive: boolean
    archived: boolean
    rejectionReason: string | null
    publicationType: TJobTeaserPublicationType | null
    publicationEndTime: string | null
    publicUrl: string | null
    lastSyncedAt: string | null
  }
  lastJob: null | {
    status: 'pending' | 'running' | 'success' | 'failed' | 'skipped'
    type: string
    errorCode: string | null
    errorMessage: string | null
    skippedReason: string | null
    finishedAt: string | null
  }
  issues: TJobBoardPostingIssue[]
}
